/**
 * O CARTÃO E AS QUATRO VISÕES DO CRM.
 *
 * As quatro leem o MESMO array de cards que veio de `GET /api/crm`. Trocar de
 * visão não busca nada: se cada uma tivesse a própria carga, o Kanban diria
 * 12 e o funil desenharia 11, e quem usa perderia a confiança nos dois.
 *
 * O que muda entre elas é a pergunta que respondem:
 *   Kanban — "como está o quadro inteiro?"        (arrastar move a etapa)
 *   Lista  — "cadê o São João?"                    (uma linha por cliente)
 *   Funil  — "onde os clientes estão empacando?"   (clicar na faixa filtra)
 *   Foco   — "o que eu faço agora?"                (uma etapa por vez)
 *
 * COMO A COR TRABALHA AQUI — a regra vale para as quatro:
 *
 *   identidade (qual etapa)   → paleta `etapa-*`, uma cor por coluna
 *   estado (como vai)         → paleta semântica: ok, warn, crit
 *
 * Cada etapa tem a SUA cor, e ela aparece em quatro lugares que o olho cruza
 * sozinho: a faixa no topo da coluna, o cabeçalho, a listra do cartão e a
 * barra do funil. Quem move um cartão de coluna vê a listra dele mudar de cor
 * — o feedback é a própria tela, sem precisar de mensagem.
 *
 * E nenhum número aparece sozinho: ao lado de cada contagem existe a frase
 * que a explica. É a mesma exigência de explicabilidade que vale para o teto
 * do número e para a liberação do chip.
 */
import { useMemo, useState, type ReactNode } from "react";
import type { CardCrm, EtapaCrm } from "@co/contracts";
import { Botao, Painel, Vazio } from "../ui/base.js";
import { corDaEtapa, iniciaisDe, semaforoDeDias } from "../ui/etapa.js";
import { num } from "../ui/formato.js";

/* ══════════════════════════════════════════════════════════════════════
   Ícones dos três botões do card
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Desenhados em SVG, não emoji.
 *
 * Emoji muda de forma em cada sistema operacional e algumas plataformas
 * renderizam o do WhatsApp como um telefone genérico — o botão mais usado da
 * tela viraria um símbolo diferente em cada máquina da equipe.
 */
function IconeWhats(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
    </svg>
  );
}

function IconeHistorico(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function IconeInfo(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   O cartão
   ══════════════════════════════════════════════════════════════════════ */

export interface AcoesDoCard {
  aoAbrirConversa: (card: CardCrm) => void;
  aoAbrirHistorico: (card: CardCrm) => void;
  aoAbrirFicha: (card: CardCrm) => void;
  aoMover: (card: CardCrm, etapaId: string) => void;
}

const MOEDA = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 0,
});

function BotaoIcone({
  rotulo,
  onClick,
  destaque,
  children,
}: {
  rotulo: string;
  onClick: () => void;
  destaque?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={rotulo}
      aria-label={rotulo}
      className={`inline-flex items-center justify-center size-8 rounded-lg transition-colors ${
        destaque
          ? "bg-ok text-superficie hover:opacity-85"
          : "bg-superficie-3 text-tinta-2 hover:bg-linha hover:text-tinta"
      }`}
    >
      {children}
    </button>
  );
}

/** Bolinha de estado + a frase que a explica. Cor sozinha não é veredito. */
function Semaforo({ dias }: { dias: number }): JSX.Element {
  const s = semaforoDeDias(dias);
  return (
    <span className={`inline-flex items-center gap-1.5 ${s.texto}`} title={s.titulo}>
      <span aria-hidden="true" className={`size-1.5 rounded-full ${s.ponto}`} />
      {s.rotulo}
    </span>
  );
}

/**
 * O cartão do cliente.
 *
 * A hierarquia de leitura foi pedida assim e é a decisão de desenho mais
 * importante da tela: RAZÃO SOCIAL em destaque, nome da pessoa logo abaixo, e
 * só então os três dados de contato. Num quadro de 40 cartões ninguém lê o
 * cartão inteiro — lê o topo, e antes do topo vê a cor e o avatar.
 */
export function Cartao({
  card,
  etapa,
  etapas,
  acoes,
  arrastavel,
  grande,
}: {
  card: CardCrm;
  etapa: EtapaCrm | undefined;
  etapas: readonly EtapaCrm[];
  acoes: AcoesDoCard;
  arrastavel?: boolean;
  grande?: boolean;
}): JSX.Element {
  const cor = corDaEtapa(etapa?.tom);

  return (
    <article
      draggable={arrastavel ?? false}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/card", card.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className={`group relative bg-superficie border rounded-[10px] overflow-hidden flex ${
        card.aguardandoResposta ? "border-ok" : "border-linha"
      } ${arrastavel ? "cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow" : ""}`}
    >
      {/* A listra é a etapa. Mover o cartão muda a cor dele: o feedback do
          movimento é a própria tela, sem precisar de mensagem. */}
      <div aria-hidden="true" className={`w-1 shrink-0 ${cor.faixa}`} />

      <div className={`flex-1 min-w-0 flex flex-col gap-2.5 ${grande ? "p-3.5" : "p-3"}`}>
        {/* ─── Identidade ───────────────────────────────────────── */}
        <div className="flex items-start gap-2.5 min-w-0">
          <span
            aria-hidden="true"
            className={`grid place-items-center rounded-lg shrink-0 font-semibold ${cor.suave} ${
              cor.tinta
            } ${grande ? "size-10 text-[13px]" : "size-9 text-[12px]"}`}
          >
            {iniciaisDe(card.razaoSocial)}
          </span>

          <div className="min-w-0 flex-1">
            <h3
              className={`font-semibold tracking-tight leading-snug break-words ${
                grande ? "text-[14.5px]" : "text-[13.5px]"
              }`}
            >
              {card.razaoSocial}
            </h3>
            {card.nome && (
              <p className="text-[12px] text-tinta-2 leading-snug truncate">{card.nome}</p>
            )}
          </div>

          {card.aguardandoResposta && (
            <span
              title="O cliente respondeu e está esperando resposta"
              className="shrink-0 inline-flex items-center gap-1 rounded-md bg-ok-suave text-ok-tinta px-1.5 py-0.5 text-[10.5px] font-medium"
            >
              <span aria-hidden="true" className="size-1.5 rounded-full bg-ok" />
              respondeu
            </span>
          )}
        </div>

        {/* ─── Contato ──────────────────────────────────────────── */}
        <dl className="text-[11.5px] flex flex-col gap-1 min-w-0 m-0">
          <div className="flex items-center gap-1.5 min-w-0 text-tinta-2">
            <dt className="sr-only">Telefone</dt>
            <dd className="m-0 tabular truncate">{card.telefoneExibicao}</dd>
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <dt className="sr-only">E-mail</dt>
            <dd className={`m-0 truncate ${card.email ? "text-tinta-2" : "text-tinta-3 italic"}`}>
              {card.email ?? "sem e-mail"}
            </dd>
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <dt className="sr-only">Operadora</dt>
            <dd className="m-0 truncate">
              {card.operadora ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-superficie-3 px-1.5 py-0.5 text-[10.5px] text-tinta-2">
                  <span aria-hidden="true">📶</span>
                  {card.operadora}
                </span>
              ) : (
                <span className="text-tinta-3 italic">operadora não informada</span>
              )}
            </dd>
          </div>
        </dl>

        {/* ─── Sinais ───────────────────────────────────────────── */}
        <div className="flex items-center gap-2.5 flex-wrap text-[11px]">
          <Semaforo dias={card.diasNaEtapa} />
          {card.valorEstimado !== null && (
            <span className="tabular text-tinta font-medium">
              {MOEDA.format(card.valorEstimado)}
            </span>
          )}
          {card.observacoes > 0 && (
            <span className="text-tinta-3" title={`${card.observacoes} observação(ões) no histórico`}>
              🗒 {card.observacoes}
            </span>
          )}
          {card.optOut && (
            <span
              title="Pediu para não receber mensagens — vale para todos os canais"
              className="inline-flex items-center gap-1 rounded-md bg-crit-suave text-crit-tinta px-1.5 py-0.5 text-[10.5px] font-medium"
            >
              não contatar
            </span>
          )}
        </div>

        {/* ─── Os três botões ───────────────────────────────────── */}
        <div className="flex items-center gap-1.5 pt-2.5 border-t border-linha">
          <BotaoIcone
            rotulo={`Conversa com ${card.razaoSocial}`}
            onClick={() => acoes.aoAbrirConversa(card)}
            destaque={card.aguardandoResposta}
          >
            <IconeWhats />
          </BotaoIcone>
          <BotaoIcone
            rotulo={`Histórico e observações de ${card.razaoSocial}`}
            onClick={() => acoes.aoAbrirHistorico(card)}
          >
            <IconeHistorico />
          </BotaoIcone>
          <BotaoIcone
            rotulo={`Informações de ${card.razaoSocial}`}
            onClick={() => acoes.aoAbrirFicha(card)}
          >
            <IconeInfo />
          </BotaoIcone>

          {/*
            O seletor existe ao lado do arrastar, não no lugar dele.
            Arrastar é bom com mouse e impossível no teclado e no celular — e
            a equipe comercial abre isso no telefone entre uma visita e outra.
          */}
          <select
            aria-label={`Mover ${card.razaoSocial} de etapa`}
            value={card.etapaId}
            onChange={(e) => acoes.aoMover(card, e.target.value)}
            className="ml-auto max-w-[48%] rounded-lg border border-linha bg-superficie-2 px-2 py-1.5 text-[11.5px] text-tinta-2 outline-none focus:border-marca truncate"
          >
            {etapas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
        </div>
      </div>
    </article>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Visão 1 — Kanban
   ══════════════════════════════════════════════════════════════════════ */

export function Kanban({
  etapas,
  cards,
  acoes,
}: {
  etapas: readonly EtapaCrm[];
  cards: readonly CardCrm[];
  acoes: AcoesDoCard;
}): JSX.Element {
  const [sobre, setSobre] = useState<string | null>(null);

  const porEtapa = useMemo(() => {
    const m = new Map<string, CardCrm[]>();
    for (const e of etapas) m.set(e.id, []);
    for (const c of cards) {
      const lista = m.get(c.etapaId);
      if (lista) lista.push(c);
      // Card em etapa que não veio na lista (desativada) não some da tela:
      // ele aparece na visão Lista. Sumir em silêncio é perder cliente.
    }
    return m;
  }, [etapas, cards]);

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-3 items-start" style={{ minWidth: etapas.length * 272 }}>
        {etapas.map((etapa) => {
          const lista = porEtapa.get(etapa.id) ?? [];
          const alvo = sobre === etapa.id;
          const cor = corDaEtapa(etapa.tom);
          return (
            <section
              key={etapa.id}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (sobre !== etapa.id) setSobre(etapa.id);
              }}
              onDragLeave={() => setSobre((s) => (s === etapa.id ? null : s))}
              onDrop={(e) => {
                e.preventDefault();
                setSobre(null);
                const id = e.dataTransfer.getData("text/card");
                const card = cards.find((c) => c.id === id);
                if (card && card.etapaId !== etapa.id) acoes.aoMover(card, etapa.id);
              }}
              className={`w-[264px] shrink-0 rounded-[10px] border-2 overflow-hidden transition-colors ${
                alvo ? `${cor.borda} ${cor.suave}` : "border-transparent bg-superficie-2"
              }`}
            >
              {/* A faixa cheia no topo é o que dá a cada coluna uma identidade
                  que se lê de longe, antes de qualquer texto. */}
              <div aria-hidden="true" className={`h-1.5 ${cor.faixa}`} />

              <header className={`px-3 py-2.5 ${cor.suave}`}>
                <div className="flex items-center gap-2">
                  <h3 className={`text-[12.5px] font-semibold tracking-tight ${cor.tinta}`}>
                    {etapa.nome}
                  </h3>
                  <span
                    className={`ml-auto rounded-full bg-superficie px-2 py-0.5 text-[11px] font-medium tabular ${cor.tinta}`}
                  >
                    {num(etapa.cards)}
                  </span>
                </div>
                {etapa.valor > 0 && (
                  <p className={`text-[11px] mt-0.5 tabular opacity-80 ${cor.tinta}`}>
                    {MOEDA.format(etapa.valor)} em jogo
                  </p>
                )}
              </header>

              {etapa.instrucao && (
                <p className="px-3 pt-2 text-[11px] text-tinta-3 leading-snug">{etapa.instrucao}</p>
              )}

              <div className="p-2.5 flex flex-col gap-2.5 min-h-[96px]">
                {lista.length === 0 ? (
                  <div
                    className={`rounded-lg border-2 border-dashed py-7 text-center text-[11.5px] ${
                      alvo ? `${cor.borda} ${cor.tinta}` : "border-linha text-tinta-3"
                    }`}
                  >
                    {alvo ? "Solte aqui ↓" : "Arraste um cartão para cá"}
                  </div>
                ) : (
                  lista.map((c) => (
                    <Cartao
                      key={c.id}
                      card={c}
                      etapa={etapa}
                      etapas={etapas}
                      acoes={acoes}
                      arrastavel
                    />
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Visão 2 — Lista (horizontal): uma linha por cliente
   ══════════════════════════════════════════════════════════════════════ */

export function Lista({
  etapas,
  cards,
  acoes,
}: {
  etapas: readonly EtapaCrm[];
  cards: readonly CardCrm[];
  acoes: AcoesDoCard;
}): JSX.Element {
  const etapaDe = useMemo(() => new Map(etapas.map((e) => [e.id, e] as const)), [etapas]);

  if (cards.length === 0) {
    return <Vazio>Nenhum cliente neste recorte.</Vazio>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left" style={{ minWidth: 980 }}>
        <thead>
          <tr className="text-[10.5px] uppercase tracking-[0.08em] text-tinta-3">
            <th className="font-semibold px-4 py-2.5">Cliente</th>
            <th className="font-semibold px-4 py-2.5">Contato</th>
            <th className="font-semibold px-4 py-2.5">Operadora</th>
            <th className="font-semibold px-4 py-2.5">Etapa</th>
            <th className="font-semibold px-4 py-2.5">Responsável</th>
            <th className="font-semibold px-4 py-2.5 text-right">Valor</th>
            <th className="font-semibold px-4 py-2.5">Situação</th>
            <th className="font-semibold px-4 py-2.5 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {cards.map((c) => {
            const etapa = etapaDe.get(c.etapaId);
            const cor = corDaEtapa(etapa?.tom);
            return (
              <tr key={c.id} className="border-t border-linha align-middle">
                <td className="px-4 py-2.5 min-w-0">
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className={`grid place-items-center size-8 rounded-lg shrink-0 text-[11px] font-semibold ${cor.suave} ${cor.tinta}`}
                    >
                      {iniciaisDe(c.razaoSocial)}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold tracking-tight leading-snug">
                        {c.razaoSocial}
                      </div>
                      {c.nome && <div className="text-[11.5px] text-tinta-2">{c.nome}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[12px] text-tinta-2">
                  <div className="tabular">{c.telefoneExibicao}</div>
                  <div className="text-tinta-3 truncate max-w-[200px]">
                    {c.email ?? "sem e-mail"}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[12px] text-tinta-2">{c.operadora ?? "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true" className={`size-2.5 rounded-full ${cor.faixa}`} />
                    <select
                      aria-label={`Etapa de ${c.razaoSocial}`}
                      value={c.etapaId}
                      onChange={(e) => acoes.aoMover(c, e.target.value)}
                      className="rounded-lg border border-linha bg-superficie-2 px-2 py-1.5 text-[12px] text-tinta outline-none focus:border-marca"
                    >
                      {etapas.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[12px] text-tinta-2">{c.responsavelNome}</td>
                <td className="px-4 py-2.5 text-[12px] text-right tabular">
                  {c.valorEstimado === null ? "—" : MOEDA.format(c.valorEstimado)}
                </td>
                <td className="px-4 py-2.5 text-[11.5px]">
                  <Semaforo dias={c.diasNaEtapa} />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5 justify-end">
                    <BotaoIcone
                      rotulo={`Conversa com ${c.razaoSocial}`}
                      onClick={() => acoes.aoAbrirConversa(c)}
                      destaque={c.aguardandoResposta}
                    >
                      <IconeWhats />
                    </BotaoIcone>
                    <BotaoIcone
                      rotulo={`Histórico de ${c.razaoSocial}`}
                      onClick={() => acoes.aoAbrirHistorico(c)}
                    >
                      <IconeHistorico />
                    </BotaoIcone>
                    <BotaoIcone
                      rotulo={`Informações de ${c.razaoSocial}`}
                      onClick={() => acoes.aoAbrirFicha(c)}
                    >
                      <IconeInfo />
                    </BotaoIcone>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Visão 3 — Funil
   ══════════════════════════════════════════════════════════════════════ */

/**
 * O funil desenhado, e os clientes da faixa clicada ao lado.
 *
 * A largura de cada faixa é proporcional à MAIOR etapa, não ao total: com 200
 * clientes em "Novo" e 3 em "Proposta", proporção sobre o total desenharia as
 * etapas finais como fios de cabelo. E cada faixa carrega o número escrito —
 * desenho sem número é impressão, não medida.
 *
 * Entre duas faixas aparece quanto passou de uma para a outra. É a pergunta
 * que o funil existe para responder: onde a operação perde gente.
 */
export function Funil({
  etapas,
  cards,
  acoes,
  etapaAberta,
  aoAbrirEtapa,
}: {
  etapas: readonly EtapaCrm[];
  cards: readonly CardCrm[];
  acoes: AcoesDoCard;
  etapaAberta: string | null;
  aoAbrirEtapa: (id: string) => void;
}): JSX.Element {
  const maior = Math.max(1, ...etapas.map((e) => e.cards));
  const selecionada = etapaAberta ?? etapas[0]?.id ?? null;
  const daEtapa = cards.filter((c) => c.etapaId === selecionada);
  const etapa = etapas.find((e) => e.id === selecionada);
  const total = etapas.reduce((s, e) => s + e.cards, 0);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(300px,400px)_1fr] items-start">
      <Painel titulo="O funil" dica={`${num(total)} no total`}>
        <div className="flex flex-col">
          {etapas.map((e, i) => {
            const cor = corDaEtapa(e.tom);
            const largura = Math.max(16, Math.round((e.cards / maior) * 100));
            const ativa = e.id === selecionada;
            const anterior = i > 0 ? etapas[i - 1] : null;
            const passou =
              anterior && anterior.cards > 0 ? Math.round((e.cards / anterior.cards) * 100) : null;

            return (
              <div key={e.id}>
                {/* Quanto passou da etapa de cima para esta. */}
                {passou !== null && (
                  <div className="flex items-center justify-center py-0.5">
                    <span
                      className="text-[10.5px] text-tinta-3 tabular"
                      title={`${e.cards} dos ${anterior?.cards} de "${anterior?.nome}" chegaram aqui`}
                    >
                      ↓ {passou}%
                    </span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => aoAbrirEtapa(e.id)}
                  aria-pressed={ativa}
                  className={`w-full text-left rounded-lg px-2 py-1.5 transition-colors ${
                    ativa ? "bg-superficie-3" : "hover:bg-superficie-2"
                  }`}
                >
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className={`text-[12.5px] font-medium tracking-tight ${cor.tinta}`}>
                      {e.nome}
                    </span>
                    <span className="ml-auto text-[11.5px] text-tinta-3 tabular">
                      {num(e.cards)}
                      {e.valor > 0 && ` · ${MOEDA.format(e.valor)}`}
                    </span>
                  </div>
                  {/* Centralizada, para o conjunto formar a silhueta do funil. */}
                  <div className="flex justify-center">
                    <div
                      className={`h-7 rounded-md grid place-items-center ${cor.faixa} ${
                        ativa ? "opacity-100 ring-2 ring-offset-1 ring-offset-superficie" : "opacity-75"
                      }`}
                      style={{ width: `${largura}%` }}
                    >
                      {e.cards > 0 && (
                        <span className="text-[11px] font-semibold text-superficie tabular">
                          {num(e.cards)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-[11.5px] text-tinta-3 mt-3 leading-relaxed">
          A largura compara com a maior etapa, não com o total — senão as etapas finais
          somem do desenho. A porcentagem entre duas faixas é quanto passou de uma para
          a outra. Clique numa faixa para ver quem está nela.
        </p>
      </Painel>

      <Painel
        titulo={etapa ? `Quem está em “${etapa.nome}”` : "Selecione uma etapa"}
        {...(etapa?.instrucao ? { dica: etapa.instrucao } : {})}
      >
        {daEtapa.length === 0 ? (
          <Vazio>Nenhum cliente nesta etapa.</Vazio>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {daEtapa.map((c) => (
              <Cartao key={c.id} card={c} etapa={etapa} etapas={etapas} acoes={acoes} />
            ))}
          </div>
        )}
      </Painel>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Visão 4 — Foco
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Uma etapa por vez, em cartões grandes.
 *
 * É a visão de quem vai TRABALHAR a lista, não de quem vai olhar o quadro.
 * Por isso a instrução da etapa aparece grande no topo, na cor dela:
 * "proposta enviada, combine a data do retorno" é o que transforma um quadro
 * bonito em ação.
 */
export function Foco({
  etapas,
  cards,
  acoes,
  etapaAberta,
  aoAbrirEtapa,
}: {
  etapas: readonly EtapaCrm[];
  cards: readonly CardCrm[];
  acoes: AcoesDoCard;
  etapaAberta: string | null;
  aoAbrirEtapa: (id: string) => void;
}): JSX.Element {
  const selecionada = etapaAberta ?? etapas[0]?.id ?? null;
  const etapa = etapas.find((e) => e.id === selecionada);
  const daEtapa = cards.filter((c) => c.etapaId === selecionada);
  const proxima = etapa
    ? etapas.find((e) => e.ordem > etapa.ordem && e.tipo === "aberta")
    : undefined;
  const cor = corDaEtapa(etapa?.tom);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 flex-wrap">
        {etapas.map((e) => {
          const c = corDaEtapa(e.tom);
          const ativa = e.id === selecionada;
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => aoAbrirEtapa(e.id)}
              aria-pressed={ativa}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[12.5px] transition-colors ${
                ativa
                  ? `${c.suave} ${c.borda} ${c.tinta} font-medium`
                  : "bg-superficie border-linha-forte text-tinta-2 hover:bg-superficie-3"
              }`}
            >
              <span aria-hidden="true" className={`size-2.5 rounded-full ${c.faixa}`} />
              {e.nome}
              <span className="text-[11px] opacity-70 tabular">{num(e.cards)}</span>
            </button>
          );
        })}
      </div>

      {etapa && (
        <div className={`rounded-[10px] px-4 py-3.5 ${cor.suave}`}>
          <p className={`text-[13.5px] leading-relaxed m-0 ${cor.tinta}`}>
            {etapa.instrucao ??
              `${daEtapa.length} cliente(s) em ${etapa.nome}. Trabalhe de cima para baixo: o primeiro é o que está parado há mais tempo.`}
          </p>
        </div>
      )}

      {daEtapa.length === 0 ? (
        <Vazio>Nenhum cliente nesta etapa. Escolha outra acima.</Vazio>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {daEtapa.map((c) => (
            <div key={c.id} className="flex flex-col gap-2">
              <Cartao card={c} etapa={etapa} etapas={etapas} acoes={acoes} grande />
              {proxima && (
                <Botao pequeno onClick={() => acoes.aoMover(c, proxima.id)}>
                  Avançar para {proxima.nome} →
                </Botao>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
